/**
 * ExtensionInstallSection — the "Install" card shared by the Baby Chops and
 * Chopbook Tool pages.
 *
 * Both pages distribute a Chrome extension from its own CWS listing, and their
 * Install sections had drifted into ~70 near-identical lines: the manual-install
 * notice, the Store CTA, the no-listing fallback, the version line, the
 * numbered steps, and the access-help footnote. Drift had already started —
 * the access-help paragraph was tested on one page and not the other.
 *
 * What is SHARED lives here. What legitimately differs stays a prop:
 *
 *   - `children`  the intro paragraph. Baby Chops says "does not ship inside
 *                 the nu-assist extension"; Chopbook has to say "or inside Baby
 *                 Chops" too. Same shape, different truth.
 *   - `steps`     Chopbook's step 2 is about signing into NU Assist, because
 *                 its auth is federated. Baby Chops has no equivalent.
 *   - `noListingCopy`  each page words its own fallback.
 *   - `showManualInstallNotice`  Baby Chops keeps the manual-install warning
 *                 (its readers came off the Drive ZIP and Load-Unpacked eras
 *                 and many still have those copies); Chopbook opted out on
 *                 2026-08-14 — see the call site.
 *
 * `testIdPrefix` reproduces the ids both pages' existing tests already assert
 * (`tool-babychops-v2-*`, `tool-chopbook-*`), so extracting this changed no
 * test expectations — which is the property that made the refactor safe to do
 * on a live page.
 *
 * The manual-install notice renders ABOVE the CTA on purpose: installing from
 * the Store does not remove a manually-loaded copy, so the warning has to be
 * read before the click. Both pages' tests assert that ordering.
 */
import type { ReactNode } from "react";
import { Puzzle } from "lucide-react";
import { ManualInstallNotice } from "./ManualInstallNotice";

export interface ExtensionInstallSectionProps {
  /** Extension name as the reader knows it, e.g. "Baby Chops". */
  extensionName: string;
  /** Test-id namespace, e.g. `tool-chopbook`. */
  testIdPrefix: string;
  /** Resolved CWS listing URL, or `null` when this stage has no listing. */
  cwsListingUrl: string | null;
  /** Latest published version, or `null` to hide the line. */
  availableVersion: string | null;
  /** Numbered install steps, in order. */
  steps: readonly string[];
  /** Copy for the no-listing branch. */
  noListingCopy: string;
  /**
   * Render the manual-install warning above the CTA. Defaults to `true` —
   * opting OUT is the deliberate act, so a new install page inherits the
   * warning rather than silently omitting it.
   */
  showManualInstallNotice?: boolean;
  /** The intro paragraph. */
  children: ReactNode;
}

export function ExtensionInstallSection({
  extensionName,
  testIdPrefix,
  cwsListingUrl,
  availableVersion,
  steps,
  noListingCopy,
  showManualInstallNotice = true,
  children,
}: ExtensionInstallSectionProps) {
  // No listing on this stage: render ONLY the honest statement of that, and
  // nothing else.
  //
  // This is the /pr-review iter 1 Critical, and the fix is structural rather
  // than a copy edit because EVERY other block in this section presupposes a
  // listing. The first version rendered the manual-install notice next to the
  // fallback, so the page said "remove your manual install" directly above
  // "load it unpacked from a local build" — telling a recruiter to delete a
  // working extension and then handing them a route they cannot follow. The
  // intro paragraph ("install it directly from that listing"), the numbered
  // steps, and the "ask in #it" access-help line were all equally untrue here.
  //
  // Early-returning is what makes that hard to reintroduce: there is no
  // arrangement of the branch below in which an install instruction survives
  // a null listing.
  if (!cwsListingUrl) {
    return (
      <section className="rounded-md border border-border bg-surface p-5">
        <h2 className="mb-2 text-h3 text-text">Install</h2>
        <p
          data-testid={`${testIdPrefix}-no-listing`}
          className="rounded-md bg-accent-muted p-3 text-body text-text-secondary"
        >
          {noListingCopy}
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-md border border-border bg-surface p-5">
      <h2 className="mb-2 text-h3 text-text">Install</h2>
      <p className="text-body text-text-secondary">{children}</p>

      {/* Above the CTA on purpose: installing from the Store does not remove a
          manually-loaded copy, so this has to be read before the click. The
          pages that show it assert the ordering. */}
      {showManualInstallNotice ? (
        <div className="mt-4">
          <ManualInstallNotice
            extensionName={extensionName}
            testId={`${testIdPrefix}-manual-install-notice`}
          />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-3">
        <a
          href={cwsListingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-md bg-accent px-5 py-2.5 text-label font-bold text-bg shadow-lg hover:bg-accent-hover"
        >
          <Puzzle size={15} aria-hidden />
          View in the Chrome Web Store
        </a>
      </div>

      {availableVersion ? (
        <p
          data-testid={`${testIdPrefix}-version`}
          className="mb-3 text-caption text-text-secondary"
        >
          Latest published version:{" "}
          <span className="font-semibold text-text">v{availableVersion}</span>
        </p>
      ) : null}

      <ol className="list-decimal space-y-1 pl-5 text-body text-text-secondary">
        {steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      {/* Without this, a recruiter who hits Google's bare "item not available"
          page cannot tell whether it is them, us, or a broken link.

          Deliberately does NOT name a cause. The previous wording — "your
          account is still being given access" — is true only for a PUBLISHED
          private listing. It is wrong for a listing that is still an
          unsubmitted draft (nobody can be granted access to a draft, so #it
          cannot help), and wrong again for someone signed into the wrong
          Chrome profile. Naming one cause sends the other two into a support
          loop, which is worse than naming none. This version states the
          symptom, gives the check the reader can do themselves, and routes
          them somewhere that can distinguish the cases. */}
      <p
        data-testid={`${testIdPrefix}-access-help`}
        className="mt-3 text-caption text-text-secondary"
      >
        If the Chrome Web Store says the item isn't available: check you're
        signed into the Store with your @nusearch.com account, and if it still
        doesn't appear, ask in #it — the listing is private, so it may not be
        shared with you yet.
      </p>
    </section>
  );
}
